import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Slider,
  Typography,
  Paper,
} from '@mui/material';
import {
  Create as DrawIcon,
  RadioButtonUnchecked as CircleIcon,
  ArrowForward as ArrowIcon,
  TextFields as TextIcon,
  Undo as UndoIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const ImageAnnotator = ({ open, onClose, imageUrl, onSave }) => {
  const canvasRef = useRef(null);
  const [ctx, setCtx] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('draw'); // draw, circle, arrow, text
  const [color, setColor] = useState('#dc3545'); // Default red for highlighting issues
  const [lineWidth, setLineWidth] = useState(10);
  const [annotations, setAnnotations] = useState([]);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState(null);
  const [image, setImage] = useState(null);

  // Load image when dialog opens
  useEffect(() => {
    if (open && imageUrl) {
      // Clear annotations when loading a new image
      setAnnotations([]);
      setCurrentAnnotation(null);

      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setImage(img);
        if (canvasRef.current) {
          const canvas = canvasRef.current;

          // Calculate dimensions to fit within modal while maintaining aspect ratio
          const maxWidth = window.innerWidth * 0.8; // 80% of viewport width
          const maxHeight = window.innerHeight * 0.7; // 70% of viewport height

          let displayWidth = img.width;
          let displayHeight = img.height;

          // Scale down if image is larger than max dimensions
          if (displayWidth > maxWidth || displayHeight > maxHeight) {
            const widthRatio = maxWidth / displayWidth;
            const heightRatio = maxHeight / displayHeight;
            const ratio = Math.min(widthRatio, heightRatio);

            displayWidth = displayWidth * ratio;
            displayHeight = displayHeight * ratio;
          }

          // Set canvas to display dimensions
          canvas.width = displayWidth;
          canvas.height = displayHeight;

          const context = canvas.getContext('2d');
          // Draw image scaled to fit canvas
          context.drawImage(img, 0, 0, displayWidth, displayHeight);
          setCtx(context);
        }
      };
      img.src = imageUrl;
    }
  }, [open, imageUrl]);

  // Redraw canvas with all annotations
  const redrawCanvas = () => {
    if (!ctx || !image) return;

    // Clear and redraw image
    const canvas = canvasRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    // Redraw all annotations
    annotations.forEach(annotation => {
      ctx.strokeStyle = annotation.color;
      ctx.lineWidth = annotation.lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      if (annotation.type === 'draw') {
        ctx.beginPath();
        annotation.points.forEach((point, index) => {
          if (index === 0) {
            ctx.moveTo(point.x, point.y);
          } else {
            ctx.lineTo(point.x, point.y);
          }
        });
        ctx.stroke();
      } else if (annotation.type === 'circle') {
        const radius = Math.sqrt(
          Math.pow(annotation.endX - annotation.startX, 2) +
          Math.pow(annotation.endY - annotation.startY, 2)
        );
        ctx.beginPath();
        ctx.arc(annotation.startX, annotation.startY, radius, 0, 2 * Math.PI);
        ctx.stroke();
      } else if (annotation.type === 'arrow') {
        drawArrow(ctx, annotation.startX, annotation.startY, annotation.endX, annotation.endY);
      } else if (annotation.type === 'text') {
        ctx.font = `${annotation.fontSize}px Arial`;
        ctx.fillStyle = annotation.color;
        ctx.fillText(annotation.text, annotation.x, annotation.y);
      }
    });
  };

  const drawArrow = (context, fromX, fromY, toX, toY) => {
    const headLength = 15;
    const angle = Math.atan2(toY - fromY, toX - fromX);

    // Draw line
    context.beginPath();
    context.moveTo(fromX, fromY);
    context.lineTo(toX, toY);
    context.stroke();

    // Draw arrow head
    context.beginPath();
    context.moveTo(toX, toY);
    context.lineTo(
      toX - headLength * Math.cos(angle - Math.PI / 6),
      toY - headLength * Math.sin(angle - Math.PI / 6)
    );
    context.moveTo(toX, toY);
    context.lineTo(
      toX - headLength * Math.cos(angle + Math.PI / 6),
      toY - headLength * Math.sin(angle + Math.PI / 6)
    );
    context.stroke();
  };

  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Handle both mouse and touch events
    let clientX, clientY;
    if (e.touches && e.touches.length > 0) {
      // Touch event
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      // Touch end event
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const handleMouseDown = (e) => {
    if (!ctx) return;

    const { x, y } = getCanvasCoordinates(e);
    setIsDrawing(true);

    if (tool === 'draw') {
      setCurrentAnnotation({
        type: 'draw',
        color,
        lineWidth,
        points: [{ x, y }],
      });
    } else if (tool === 'circle' || tool === 'arrow') {
      setCurrentAnnotation({
        type: tool,
        color,
        lineWidth,
        startX: x,
        startY: y,
        endX: x,
        endY: y,
      });
    } else if (tool === 'text') {
      setTextPosition({ x, y });
      setIsDrawing(false);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !ctx) return;

    const { x, y } = getCanvasCoordinates(e);

    if (tool === 'draw') {
      setCurrentAnnotation(prev => ({
        ...prev,
        points: [...prev.points, { x, y }],
      }));

      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const points = currentAnnotation.points;
      if (points.length > 0) {
        ctx.beginPath();
        ctx.moveTo(points[points.length - 1].x, points[points.length - 1].y);
        ctx.lineTo(x, y);
        ctx.stroke();
      }
    } else if (tool === 'circle' || tool === 'arrow') {
      setCurrentAnnotation(prev => ({
        ...prev,
        endX: x,
        endY: y,
      }));

      // Redraw to show preview
      redrawCanvas();

      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;

      if (tool === 'circle') {
        const radius = Math.sqrt(
          Math.pow(x - currentAnnotation.startX, 2) +
          Math.pow(y - currentAnnotation.startY, 2)
        );
        ctx.beginPath();
        ctx.arc(currentAnnotation.startX, currentAnnotation.startY, radius, 0, 2 * Math.PI);
        ctx.stroke();
      } else if (tool === 'arrow') {
        drawArrow(ctx, currentAnnotation.startX, currentAnnotation.startY, x, y);
      }
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing) return;

    setIsDrawing(false);

    if (currentAnnotation && currentAnnotation.type !== 'text') {
      setAnnotations(prev => [...prev, currentAnnotation]);
      setCurrentAnnotation(null);
    }
  };

  // Touch event handlers for mobile devices
  const handleTouchStart = (e) => {
    e.preventDefault(); // Prevent scrolling while drawing
    handleMouseDown(e);
  };

  const handleTouchMove = (e) => {
    e.preventDefault(); // Prevent scrolling while drawing
    handleMouseMove(e);
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    handleMouseUp();
  };

  const handleTextSubmit = () => {
    if (!textInput || !textPosition) return;

    const textAnnotation = {
      type: 'text',
      color,
      text: textInput,
      x: textPosition.x,
      y: textPosition.y,
      fontSize: 20,
    };

    setAnnotations(prev => [...prev, textAnnotation]);

    // Draw text
    ctx.font = '20px Arial';
    ctx.fillStyle = color;
    ctx.fillText(textInput, textPosition.x, textPosition.y);

    // Reset
    setTextInput('');
    setTextPosition(null);
  };

  const handleUndo = () => {
    if (annotations.length === 0) return;

    setAnnotations(prev => prev.slice(0, -1));
    setTimeout(redrawCanvas, 0);
  };

  const handleClear = () => {
    setAnnotations([]);
    if (ctx && image) {
      const canvas = canvasRef.current;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    }
  };

  const handleSave = () => {
    if (!canvasRef.current) return;

    // Convert canvas to blob
    canvasRef.current.toBlob((blob) => {
      onSave(blob);
    }, 'image/jpeg', 0.95);
  };

  const handleClose = () => {
    setAnnotations([]);
    setCurrentAnnotation(null);
    setTextPosition(null);
    setTextInput('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Annotate Image
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Tool Selection */}
              <ToggleButtonGroup
                value={tool}
                exclusive
                onChange={(e, newTool) => newTool && setTool(newTool)}
                size="small"
              >
                <ToggleButton value="draw">
                  <DrawIcon sx={{ mr: 0.5 }} /> Draw
                </ToggleButton>
                <ToggleButton value="circle">
                  <CircleIcon sx={{ mr: 0.5 }} /> Circle
                </ToggleButton>
                <ToggleButton value="arrow">
                  <ArrowIcon sx={{ mr: 0.5 }} /> Arrow
                </ToggleButton>
                <ToggleButton value="text">
                  <TextIcon sx={{ mr: 0.5 }} /> Text
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Color Selection */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {['#dc3545', '#ffc107', '#28a745', '#ffffff', '#000000'].map(c => (
                  <Box
                    key={c}
                    onClick={() => setColor(c)}
                    sx={{
                      width: 32,
                      height: 32,
                      backgroundColor: c,
                      border: color === c ? '3px solid #2c3e50' : '1px solid #ccc',
                      borderRadius: 1,
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </Box>

              {/* Line Width */}
              <Box sx={{ minWidth: 120 }}>
                <Typography variant="caption">Line Width: {lineWidth}px</Typography>
                <Slider
                  value={lineWidth}
                  onChange={(e, val) => setLineWidth(val)}
                  min={1}
                  max={10}
                  size="small"
                />
              </Box>

              {/* Actions */}
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <Button startIcon={<UndoIcon />} onClick={handleUndo} size="small">
                  Undo
                </Button>
                <Button startIcon={<ClearIcon />} onClick={handleClear} size="small">
                  Clear All
                </Button>
              </Box>
            </Box>

            {/* Text Input Dialog */}
            {textPosition && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Enter text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleTextSubmit()}
                  autoFocus
                />
                <Button onClick={handleTextSubmit} variant="contained" size="small">
                  Add
                </Button>
                <Button onClick={() => setTextPosition(null)} size="small">
                  Cancel
                </Button>
              </Box>
            )}
          </Paper>

          {/* Canvas */}
          <Box
            sx={{
              border: '2px solid #ccc',
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: '60vh',
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
            }}
          >
            <canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
              style={{
                cursor: 'crosshair',
                display: 'block',
                touchAction: 'none', // Prevent default touch behaviors
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Save Annotated Image
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageAnnotator;
